import { useEffect, useRef, useState, useCallback } from "react";
/**
 * CustomCombobox - A reusable combobox component with search and clear functionality.
 * Props:
 * - options: Array of option objects to display in the dropdown.
 * - label: The key in the option objects to display as text. 
 * - textField: The key in the option objects to use as the value when an option is selected. If not provided, 'label' will be used.
 * - id: Unique identifier for the input and dropdown (used for accessibility).
 * - onValueChanged: Callback function called when the selected value changes. Receives the selected option object and its display text as arguments.
 * - placeholder: Placeholder text for the input field when it's empty.
 * - defaultText: Initial text to display in the input field (overrides defaultItem if provided). 
 * - defaultItem: Initial selected option object. If provided, its label/text will be shown in the input on initial render (overridden by defaultText).
 *  - isSearchable: If true, the dropdown options will be filtered based on the input text. If false, all options will be shown regardless of input.
 * - allowClear: If true, a clear button will appear when there is text in the input, allowing the user to clear the selection.
 * - comboBoxOnly: If true, the input value will be cleared and all dropdown options will be shown each time the dropdown is opened. If false, the input value will be preserved and filtering will be applied based on the input text.
 * Example usage:
 * <CustomCombobox
 *   options={[{ id: 1, name: 'Option 1' }, { id: 2, name: 'Option 2' }]} 
 * */
const CustomCombobox = ({
  options = [],
  label,
  textField,
  id,
  onValueChanged,
  placeholder,
  defaultText = "",
  defaultItem = null,
  isSearchable = true,
  allowClear = true,
  comboBoxOnly = false,// if true, filter query is cleared and all dropdown options are shown on each open, otherwise input value is preserved and filtering is applied
}) => {
  const [query, setQuery] = useState(defaultText || defaultItem?.[label] || "");
  const [selectedItem, setSelectedItem] = useState(defaultItem || null);
  const [defaultPlaceholder, setDefaultPlaceholder] = useState(placeholder || null);

  const [isOpen, setIsOpen] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(-1);

  const isInternalChange = useRef(false);
  const suppresClearOnClose = useRef(false);


  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);

  //placeholder name logic by default if its not set by parent
  useEffect(() => {



    if (!placeholder) {

      if (comboBoxOnly == true) {
        setDefaultPlaceholder("აირჩიეთ...");
        return;
      }

      if (options.length > 0) {
        setDefaultPlaceholder("ჩაწერეთ ან აირჩიეთ...");
      }
      else {
        setDefaultPlaceholder("ჩაწერეთ...");
      }
    }

  });

  //if defaultText changes externally, update input value (but not if we caused this change ourselves by selecting an option)
  useEffect(() => {

    if (isInternalChange.current) {
      isInternalChange.current = false;
      return; // skip — we caused this change ourselves
    }
    suppresClearOnClose.current = true;
    setQuery(defaultText || defaultItem?.[label] || "");
    // console.log("defaultText changed, updating query to:", defaultText);
    setSelectedItem(null); // clear selected item if defaultText changes externally
  }, [defaultText]);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        close();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [selectedItem]);

  const getFiltered = useCallback(() => {

    if (!isSearchable || !query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (opt) => opt[label] && opt[label].toLowerCase().includes(q)
    );
  }, [options, query, isSearchable, label]);

  const open = () => {

    if (comboBoxOnly) {
      setQuery("");
    }

    setIsOpen(true);
    setFocusedIdx(-1);
  };

  const close = () => {
    setIsOpen(false);
    setFocusedIdx(-1);
    // Restore input to selected value on close
    if (selectedItem) {
      setQuery(selectedItem[textField] || selectedItem[label]);
    } else if (comboBoxOnly && !suppresClearOnClose.current) {
      //console.log(defaultText)
      setQuery(""); // ← typed text that didn't result in a selection gets cleared
      suppresClearOnClose.current = false;
    }
  };

  const handleSelect = (option) => {

    setSelectedItem(option);
    setQuery(option[textField] || option[label]);
    setIsOpen(false);
    setFocusedIdx(-1);
    isInternalChange.current = true; // mark as internal before triggering parent
    onValueChanged?.(option, option[textField] || option[label]);
  };

  const handleTextChange = (e) => {

    const value = e.target.value;
    setQuery(value);
    setSelectedItem(null);
    setFocusedIdx(-1);
    if (!isOpen) setIsOpen(true);

    if (comboBoxOnly) {
      // If typed text no longer matches current selection → clear parent
      if (selectedItem) {
        setSelectedItem(null);
        isInternalChange.current = true;
        onValueChanged?.(undefined, ""); // ← parent value cleared immediately
      }
      return; // still don't forward free text as a value
    }
    isInternalChange.current = true; // mark as internal before triggering parent
    onValueChanged?.(undefined, value);
  };

  const handleClear = (e) => {
    e.preventDefault();
    setQuery("");
    setSelectedItem(null);
    setIsOpen(false);
    onValueChanged?.(undefined, "");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    const filtered = getFiltered();

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) { open(); return; }
      const next = Math.min(focusedIdx + 1, filtered.length - 1);
      setFocusedIdx(next);
      dropdownRef.current?.children[next]?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(focusedIdx - 1, 0);
      setFocusedIdx(prev);
      dropdownRef.current?.children[prev]?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      if (isOpen && focusedIdx >= 0 && filtered[focusedIdx]) {
        handleSelect(filtered[focusedIdx]);
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  const filtered = getFiltered();
  const hasValue = Boolean(query);

  return (
    <div ref={rootRef} className="cb-root" style={{ position: "relative" }}>
      {/* Input row */}
      <div
        className="cb-input-wrap"
        style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          background: "#fff",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={`${id}-listbox`}
          value={query}
          placeholder={defaultPlaceholder}
          onChange={handleTextChange}
          onFocus={open}
          onClick={() => { if (!isOpen) open(); }}
          onKeyDown={handleKeyDown}
          style={{
            flex: 1,
            border: "none",
            background: "transparent",
            padding: "8px 12px",
            outline: "none",
            minWidth: 0,
          }}
        />

        {/* Clear button */}
        {hasValue && allowClear && (
          <button
            type="button"
            onMouseDown={handleClear}
            tabIndex={-1}
            aria-label="Clear"
            style={{
              background: "none",
              border: "none",
              padding: "0 6px",
              cursor: "pointer",
              color: "#9ca3af",
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}

        {/* Chevron */}
        {(options.length > 0) && (
          <div
            aria-hidden="true"
            style={{
              padding: "0 10px 0 4px",
              color: "#9ca3af",
              display: "flex",
              alignItems: "center",
              transition: "transform 0.2s",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              pointerEvents: "none",
            }}
          >
            ▾
          </div>)}
      </div>

      {/* Dropdown */}
      {isOpen && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          id={`${id}-listbox`}
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            maxHeight: "220px",
            overflowY: "auto",
            zIndex: 100,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: "10px 12px",

                color: "#9ca3af",
              }}
            >
              No results found
            </div>
          ) : (
            filtered.map((option, idx) => (
              <div
                key={`${id}-${idx}`}
                role="option"
                aria-selected={option === selectedItem}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(option); }}
                style={{
                  padding: "8px 12px",

                  cursor: "pointer",
                  background:
                    idx === focusedIdx
                      ? "#f3f4f6"
                      : option === selectedItem
                        ? "#eff6ff"
                        : "transparent",
                  color: option === selectedItem ? "#1d4ed8" : "#111827",
                  fontWeight: option === selectedItem ? 500 : 400,
                  transition: "background 0.1s",
                }}
                onMouseEnter={() => setFocusedIdx(idx)}
                onMouseLeave={() => setFocusedIdx(-1)}
              >
                {option[label]}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CustomCombobox;
